/**
 * The onboarding view's tabs, in order. `flag` is the organization_carrier_statuses column that puts a carrier in the
 * tab, and `subtitle` is the short description shown above its list. The class strings tint the tab and its panel.
 * `subBuckets` are the tab's sections in display order, each with the view column that says a carrier meets its rule;
 * a carrier meeting several shows in the last one (subBucketFor).
 */
export const BUCKETS = [
    {
        key: 'ACCOUNT_SETUP',
        label: 'Account Setup',
        flag: 'in_account_setup',
        subtitle: "Fulfillment Partner and Motor Carrier accounts that aren't approved and set up yet",
        tabClass: 'border-slate-300 bg-white text-slate-900',
        panelClass: 'border-slate-300 bg-white',
        subBuckets: [
            { key: 'UPLOADED', label: 'Uploaded', flag: 'setup_uploaded' },
            { key: 'INVITED', label: 'Invited', flag: 'setup_invited' },
            { key: 'IN_PROGRESS', label: 'In Progress', flag: 'setup_in_progress' },
            { key: 'AWAITING_COMPLIANCE', label: 'Awaiting Compliance', flag: 'setup_awaiting_compliance' },
        ],
    },
    {
        key: 'COMPLIANCE',
        label: 'Compliance',
        flag: 'in_compliance',
        subtitle: 'All Fulfillment Partners and Motor Carriers and where they stand with compliance',
        tabClass: 'border-blue-300 bg-blue-50 text-blue-800',
        panelClass: 'border-blue-300 bg-blue-50',
        subBuckets: [
            { key: 'AWAITING_REVIEW', label: 'Awaiting Review', flag: 'compliance_awaiting_review' },
            { key: 'APPROVED', label: 'Approved', flag: 'compliance_approved' },
            { key: 'DENIED', label: 'Denied', flag: 'compliance_denied' },
        ],
    },
    {
        key: 'INTEGRATIONS',
        label: 'Integrations & Pricing',
        flag: 'in_integrations',
        subtitle: 'Fulfillment Partners and Motor Carriers that still need pricing and integrations setup',
        tabClass: 'border-violet-300 bg-violet-50 text-violet-800',
        panelClass: 'border-violet-300 bg-violet-50',
        subBuckets: [
            { key: 'AWAITING_INFORMATION', label: 'Awaiting Information', flag: 'integrations_awaiting_information' },
            { key: 'IN_PROGRESS', label: 'In Progress', flag: 'integrations_in_progress' },
            { key: 'INTEGRATION_LIVE', label: 'Integration Live', flag: 'integrations_live' },
        ],
    },
    {
        key: 'LIVE',
        label: 'Live',
        flag: 'is_live',
        subtitle: 'Motor Carriers and Fulfillment Partners that are set up, approved and integrated',
        tabClass: 'border-green-300 bg-green-50 text-green-800',
        panelClass: 'border-green-300 bg-green-50',
        subBuckets: [
            { key: 'READY_TO_LINK', label: 'Ready To Link', flag: 'live_ready_to_link' },
            { key: 'LIVE', label: 'Live', flag: 'live_linked' },
        ],
    },
];

/** The organization_carrier_statuses relationships the carrier onboarding view lists. Flex Centers (OWNED) aren't listed. */
export const ONBOARDING_RELATIONSHIPS = ['PARTNER', 'CONTRACTOR'];

/** Card subtitle for each organization_carrier_statuses `relationship`. */
export const RELATIONSHIP_SUBTITLES = {
    OWNED: 'Flex Center',
    PARTNER: 'Fulfillment Partner',
    CONTRACTOR: 'Motor Carrier',
};

/** Badge colours in the order the info dialog lists them: green, blue, purple, yellow, red. */
export const BADGE_VARIANT_ORDER = ['primary', 'info', 'accent', 'warning', 'destructive'];

/**
 * Card badges, in display order. `flag` is the view column that shows the badge; `variant` is the design-system Tag
 * variant.
 */
export const BADGES = [
    { key: 'INVITED', label: 'Invited', flag: 'badge_invited', variant: 'warning' },
    {
        key: 'ACCOUNT_INFO_NEEDED',
        label: 'Account Information Needed',
        flag: 'badge_account_info_needed',
        variant: 'warning',
    },
    { key: 'REQUESTED_DOCUMENTS', label: 'Requested Documents', flag: 'badge_requested_documents', variant: 'warning' },
    { key: 'IN_COMPLIANCE', label: 'In Compliance', flag: 'badge_in_compliance', variant: 'info' },
    { key: 'APPROVED', label: 'Approved By Compliance', flag: 'badge_approved', variant: 'primary' },
    { key: 'REJECTED', label: 'Rejected By Compliance', flag: 'badge_rejected', variant: 'destructive' },
    { key: 'NEEDS_PRICING', label: 'Needs Pricing', flag: 'badge_needs_pricing', variant: 'warning' },
    // The view's `badge_integration_pending` is Flex Centers only.
    { key: 'INTEGRATION', label: 'Integration Not Live', flag: 'badge_integration_not_live', variant: 'warning' },
    {
        key: 'NO_INTEGRATIONS',
        label: 'Integrations Not Required',
        flag: 'badge_no_integrations',
        variant: 'accent',
    },
    { key: 'LIVE', label: 'Live', flag: 'badge_live', variant: 'primary' },
];

/** The shipper onboarding view's tabs, in order, shaped like BUCKETS; `flag` is an organization_shipper_statuses column. */
export const SHIPPER_BUCKETS = [
    {
        key: 'ACCOUNT_SETUP',
        label: 'Account Setup',
        flag: 'in_account_setup',
        subtitle: 'Shippers that still need pricing, connected carriers or a delivery profile',
        tabClass: 'border-slate-300 bg-white text-slate-900',
        panelClass: 'border-slate-300 bg-white',
    },
    {
        key: 'INTEGRATIONS',
        label: 'Integrations',
        flag: 'in_integrations',
        subtitle: "Shippers with no integration set up, or one that's failing or needs info",
        tabClass: 'border-violet-300 bg-violet-50 text-violet-800',
        panelClass: 'border-violet-300 bg-violet-50',
    },
    {
        key: 'LIVE',
        label: 'Live',
        flag: 'is_live',
        subtitle: 'Shippers that are set up and live',
        tabClass: 'border-green-300 bg-green-50 text-green-800',
        panelClass: 'border-green-300 bg-green-50',
    },
];

/** Shipper card badges, in display order, shaped like BADGES. */
export const SHIPPER_BADGES = [
    {
        key: 'NO_INTEGRATIONS_SETUP',
        label: 'No Integrations Setup',
        flag: 'badge_no_integrations_setup',
        variant: 'warning',
    },
    {
        key: 'INTEGRATION_FAILING',
        label: 'Failing Integrations',
        flag: 'badge_integration_failing',
        variant: 'destructive',
    },
    {
        key: 'INTEGRATION_NEEDS_INFO',
        label: 'Integration Needs Info',
        flag: 'badge_integration_needs_info',
        variant: 'warning',
    },
    { key: 'NO_PRICING', label: 'No Pricing Setup', flag: 'badge_no_pricing', variant: 'warning' },
    {
        key: 'NO_CONNECTED_CARRIERS',
        label: 'No Connected Carriers',
        flag: 'badge_no_connected_carriers',
        variant: 'warning',
    },
    {
        key: 'CARRIERS_MISSING_PRICING',
        label: 'Carriers Missing Pricing',
        flag: 'badge_carriers_missing_pricing',
        variant: 'warning',
    },
    { key: 'NO_DELIVERY_PROFILE', label: 'No Delivery Profile', flag: 'badge_no_delivery_profile', variant: 'warning' },
    {
        key: 'NO_INTEGRATIONS',
        label: 'Integrations Not Required',
        flag: 'badge_no_integrations',
        variant: 'accent',
    },
    { key: 'NO_ORDERS', label: 'No Orders Yet', flag: 'badge_no_orders', variant: 'info' },
    { key: 'LIVE', label: 'Live', flag: 'badge_live', variant: 'primary' },
];

/** The badge for each status a Motor Carrier's Flex Center partnership carries. */
export const COMPLIANCE_STATUS_BADGES = {
    PENDING: BADGES.find((badge) => badge.key === 'IN_COMPLIANCE'),
    APPROVED: BADGES.find((badge) => badge.key === 'APPROVED'),
    DENIED: BADGES.find((badge) => badge.key === 'REJECTED'),
};

/** Integration badge label for each `integration_badge_status` that isn't LIVE. */
export const INTEGRATION_BADGE_LABELS = {
    NOT_STARTED: 'Integration Not Started',
    FAILING: 'Integration Failing',
    NEEDS_INFO: 'Integration Needs Info',
    REQUEST_SENT: 'Integration Requested',
    OPENED: 'Integration Opened',
    DETAILS_RECEIVED: 'Integration Details Received',
    TESTING: 'Integration Testing',
    ENGINEER_REVIEW: 'Integration Engineer Review',
};
